import { HTMLAttributes, useState } from 'react'
import { useMedia } from 'react-use'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import Typography from '@/components/base/Typography'

import { accordionRevealAnimation } from './animations'
import { cn } from '@/lib/utils'

export type AccordionProps = HTMLAttributes<HTMLDivElement> & {
  columns: string
  items: {
    title: string
    text: string
  }[]
}

const AccordionItem = ({
  title,
  text,
  isOpen
}: {
  title: string
  text: string
  isOpen: boolean
}) => {
  const [open, setIsOpen] = useState(isOpen)

  return (
    <div className="border-b border-dashed border-white-dark/20">
      <button
        className="flex w-full cursor-pointer items-center justify-between py-spacing-sm"
        onClick={() => setIsOpen(!open)}
      >
        <Typography variant="large" className="font-medium text-text-accent-light">
          {title}
        </Typography>
        <span
          className={cn([
            'relative flex h-[1.2rem] w-[1.2rem] shrink-0',
            'before:absolute before:left-1/2 before:top-1/2 before:h-[.2rem] before:w-[1.2rem] before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-white-dark group-[.modal]:before:bg-white-dark',
            'after:absolute after:left-1/2 after:top-1/2 after:h-[.2rem] after:w-[1.2rem] after:-translate-x-1/2 after:-translate-y-1/2 after:rotate-90 after:rounded-full after:bg-white-dark group-[.modal]:after:bg-white-dark',
            'before:transition-transform before:duration-300 before:ease-in-out-cubic',
            'after:transition-transform after:duration-300 after:ease-in-out-cubic',
            {
              'before:rotate-180 after:rotate-180': open
            }
          ])}
        />
      </button>
      <div
        className={cn([
          'grid overflow-hidden',
          'transition-all duration-300 ease-in-out-cubic',
          {
            'grid-rows-[0fr] opacity-0': !open,
            'grid-rows-[1fr] opacity-100': open
          }
        ])}
      >
        <div className="overflow-hidden">
          <div className="pb-spacing-sm pt-spacing-xxs">
            <Typography variant="regular">{text}</Typography>
          </div>
        </div>
      </div>
    </div>
  )
}

const Accordion = ({ columns, items, ...props }: AccordionProps) => {
  const isMobile = useMedia('(max-width: 767px)', false)

  return (
    <Motion animation={accordionRevealAnimation}>
      <Section {...props}>
        <Container>
          <Grid>
            {isMobile ? (
              <div className={cn('col-span-4')}>
                {items.map((item, index) => (
                  <AccordionItem
                    isOpen={index === 0}
                    key={index}
                    title={item.title}
                    text={item.text}
                  />
                ))}
              </div>
            ) : (
              <>
                <div
                  className={cn('md:col-span-6 lg:col-span-12', columns === '2' && 'lg:col-span-6')}
                >
                  {items.map(
                    (item, index) =>
                      index % 2 === 0 && (
                        <AccordionItem
                          isOpen={index === 0}
                          key={index}
                          title={item.title}
                          text={item.text}
                        />
                      )
                  )}
                </div>
                <div
                  className={cn('md:col-span-6 lg:col-span-12', columns === '2' && 'lg:col-span-6')}
                >
                  {items.map(
                    (item, index) =>
                      index % 2 === 1 && (
                        <AccordionItem
                          isOpen={index === 0}
                          key={index}
                          title={item.title}
                          text={item.text}
                        />
                      )
                  )}
                </div>
              </>
            )}
          </Grid>
        </Container>
      </Section>
    </Motion>
  )
}

export default Accordion
